<template>
    <div>
      <!-- Optional label -->  
      <b-input-group :class="inputGroupClass">
        <!-- Text input masked as MM/DD/YYYY -->
        <b-form-input
          :id="inputId"
          v-model="internalInput"
          type="text"
          :placeholder="placeholder"
          autocomplete="off"
          v-mask="maskPattern"
          @blur="onBlur"
        ></b-form-input>
  
        <!-- Calendar Datepicker (syncs via internalDate in YYYY-MM-DD) -->
        <b-input-group-append>
          <b-form-datepicker
            v-model="internalDate"
            button-only
            right
            locale="en-US"
            aria-controls="inputId"
            @context="onContext"
          ></b-form-datepicker>
        </b-input-group-append>
      </b-input-group>
  
      <!-- Debug info (optional) -->
      <div v-if="debug">
        <p><strong>Parent v-model:</strong> {{ value }}</p>
        <p><strong>Internal Input (MM/DD/YYYY):</strong> {{ internalInput }}</p>
        <p><strong>Internal Date (YYYY-MM-DD):</strong> {{ internalDate }}</p>
        <p><strong>Picker Formatted:</strong> {{ formatted }}</p>
        <p><strong>Picker Selected (YMD):</strong> {{ selected }}</p>
      </div>
    </div>
  </template>
  
  <script>

  export default {
    name: "DatePicker",
    props: {
      /**
       * The v-model binding from the parent.
       * This should hold the date string in MM/DD/YYYY format.
       */
      value: {
        type: String,
        default: ""
      },
      /**
       * Display label text
       */
      label: {
        type: String,
        default: "Choose a date"
      },
      /**
       * Placeholder for the text input
       */
      placeholder: {
        type: String,
        default: "MM/DD/YYYY"
      },
      /**
       * DOM id for the input (useful for accessibility)
       */
      inputId: {
        type: String,
        default: "date-input"
      },
      /**
       * Additional classes for the input-group
       */
      inputGroupClass: {
        type: [String, Array, Object],
        default: () => "mb-3"
      },
      /**
       * The mask pattern from vue-the-mask for US date
       */
      maskPattern: {
        type: String,
        default: "##/##/####"
      },
      /**
       * Whether to show debug info below
       */
      debug: {
        type: Boolean,
        default: false
      }
    },
    data() {
      return {
        // The text in the input, always in MM/DD/YYYY format
        internalInput: this.value,
        // The datepicker expects YYYY-MM-DD or an empty string
        internalDate: "",
        // Additional info from the datepicker context
        formatted: "",
        selected: ""
      };
    },
    watch: {
      /**
       * Watch parent v-model (MM/DD/YYYY).
       * When parent changes, update input & internal date.
       */
      value: {
        immediate: true,
        handler(newVal) {
          // 1) Update input field to match newVal directly
          this.internalInput = newVal || "";
  
          // 2) Convert to YYYY-MM-DD for the datepicker
          const ymd = this.mmddyyyyToYmd(newVal);
          this.internalDate = ymd;
        }
      },
  
      /**
       * Watch the masked input (MM/DD/YYYY).
       * Convert it to YYYY-MM-DD and update the datepicker.
       * Emit new value in parent (v-model).
       */
      internalInput(newVal) {
        const ymd = this.mmddyyyyToYmd(newVal);
  
        // Keep datepicker in sync
        // (Only update if it differs, to avoid circular watchers)
        if (ymd !== this.internalDate) {
          this.internalDate = ymd;
        }
  
        // Emit the new masked value up to parent
        this.$emit("input", newVal);
      },
  
      /**
       * Watch datepicker value (YYYY-MM-DD).
       * Convert it to MM/DD/YYYY for the text input.
       */
      internalDate(newVal) {
        // Convert to masked format
        const mmddyyyy = this.ymdToMmddyyyy(newVal);
  
        // Update input if different
        if (mmddyyyy !== this.internalInput) {
          this.internalInput = mmddyyyy;
        }
      }
    },
    methods: {
      /**
       * Fired by the datepicker on any navigation/selection action
       */
      onContext(ctx) {
        // The date formatted in the locale
        this.formatted = ctx.selectedFormatted || "";
        // The 'YYYY-MM-DD' or empty if invalid
        this.selected = ctx.selectedYMD || "";
      },
      /**
       * Simple conversion from MM/DD/YYYY to YYYY-MM-DD (if valid)
       */
      mmddyyyyToYmd(dateStr) {
        if (!dateStr) return "";
        // Basic check with regex or string split
        const parts = dateStr.split("/");
        if (parts.length !== 3) return "";
  
        let [mm, dd, yyyy] = parts;
        // Pad with zero if needed
        mm = mm.padStart(2, "0");
        dd = dd.padStart(2, "0");
  
        // Quick validity check (month 1-12, day 1-31, year length ≥ 4, etc.)
        if (
          +mm < 1 ||
          +mm > 12 ||
          +dd < 1 ||
          +dd > 31 ||
          yyyy.length < 4
        ) {
          return "";
        }
  
        return `${yyyy}-${mm}-${dd}`;
      },
      /**
       * Simple conversion from YYYY-MM-DD to MM/DD/YYYY
       */
      ymdToMmddyyyy(ymdStr) {
        if (!ymdStr) return "";
        const parts = ymdStr.split("-");
        if (parts.length !== 3) return "";
        const [yyyy, mm, dd] = parts;
        return `${mm}/${dd}/${yyyy}`;
      },
      onBlur() {
        // Optional: validate or auto-correct the entered date on blur if needed
      }
    }
  };
  </script>
  
  <style scoped>
  /* Add any custom styles here if needed */
  </style>
  